import { cacheExchange, Client, createClient, fetchExchange } from '@urql/core';
import { persistedExchange } from '@urql/exchange-persisted';

import { contentfulGraphQLURL } from 'codegen';

const urqlPersistedExchange = persistedExchange({
	preferGetForPersistedQueries: true,
	enforcePersistedQueries: false,
});

const urqlExchanges = [cacheExchange, urqlPersistedExchange, fetchExchange];

const graphQlClient = createClient({
	url: contentfulGraphQLURL,
	exchanges: urqlExchanges,
	requestPolicy: 'network-only',
	fetchOptions: () => {
		return {
			headers: {
				Authorization: `Bearer ${process.env.CONTENTFUL_ACCESS_TOKEN}`,
				'content-type': 'application/json',
			},
		};
	},
});

const previewGraphQlClient = createClient({
	url: contentfulGraphQLURL,
	exchanges: urqlExchanges,
	requestPolicy: 'network-only',
	fetchOptions: () => {
		return {
			headers: {
				Authorization: `Bearer ${process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN}`,
				'content-type': 'application/json',
			},
		};
	},
});

export const client = graphQlClient;
export const previewClient = previewGraphQlClient;

export const asyncClient = async () => {
	const accessToken = process.env.TAG_CONTENTFUL_ACCESS_TOKEN;

	if (!accessToken) {
		return null;
	}
	const graphQlClient = createClient({
		url: contentfulGraphQLURL,
		exchanges: urqlExchanges,
		requestPolicy: 'network-only',
		fetchOptions: () => {
			return {
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'content-type': 'application/json',
				},
			};
		},
	});
	return graphQlClient;
};

export const asyncPreviewClient = async () => {
	const previewAccessToken = process.env.TAG_CONTENTFUL_PREVIEW_ACCESS_TOKEN;
	if (!previewAccessToken) {
		return null;
	}
	const previewGraphQlClient = createClient({
		url: contentfulGraphQLURL,
		exchanges: urqlExchanges,
		requestPolicy: 'network-only',
		fetchOptions: () => {
			return {
				headers: {
					Authorization: `Bearer ${previewAccessToken}`,
					'content-type': 'application/json',
				},
			};
		},
	});

	return previewGraphQlClient;
};

let urqlClient: Client | null = null;
let urqlPreviewClient: Client | null = null;

export const getClient = async (preview: boolean) => {
	if (preview) {
		if (urqlPreviewClient === null) {
			urqlPreviewClient = await asyncPreviewClient();
		}
		return urqlPreviewClient;
	} else {
		if (urqlClient === null) {
			urqlClient = await asyncClient();
		}
		return urqlClient;
	}
};

let beffeGraphQlClient: Client | null = null;

export const getBeffeGraphQlClient = (url: string) => {
	if (beffeGraphQlClient === null) {
		beffeGraphQlClient = createClient({
			url,
			exchanges: [cacheExchange, fetchExchange],
			requestPolicy: 'network-only',
			fetchOptions: () => {
				return {
					headers: {
						'content-type': 'application/json',
					},
				};
			},
		});
	}
	return beffeGraphQlClient;
};

export interface ConsentManagementPayload {
	purposes: Array<{
		Id: string;
		TransactionType: 'CONFIRMED' | 'NOT_GIVEN';
		ConsentType: 'IMPLICIT' | 'EXPLICIT';
	}>;
	source: string;
	externalId: string | null;
	dsDataElements: {
		Email: string;
		Mobile: string;
		FirstName: string;
		LastName: string;
		DateOfBirth: string | Date;
		Address: string;
		City: string;
		State: string;
		Zip: string;
	};
}

export interface ConsentManagementVariables {
	brand: 'CLEAR_CHOICE';
	payload: ConsentManagementPayload;
}

export interface ConsentManagementResponse {
	consentManagement: {
		message: string;
		success: boolean;
	};
}

const CONSENT_MANAGEMENT_MUTATION = `
  mutation ConsentManagement($brand: BffBrandType!, $payload: JSON!) {
    consentManagement(brand: $brand, payload: $payload) {
      message
      success
    }
  }
`;

export const submitConsentManagement = async (
	formId: string,
	externalId: string | null,
	userDetails: {
		Email: string;
		Mobile: string;
		FirstName: string;
		LastName: string;
		DateOfBirth: string | Date;
		Address: string;
		City: string;
		State: string;
		Zip: string;
	}
): Promise<ConsentManagementResponse | null> => {
	try {
		console.log('Creating BEFFE client...');
		const beffeClient = getBeffeGraphQlClient('https://wwwstg.clearchoice.com/api/bff');

		const variables: ConsentManagementVariables = {
			brand: 'CLEAR_CHOICE',
			payload: {
				purposes: [
					{
						Id: 'Transactional_Email',
						TransactionType: 'CONFIRMED',
						ConsentType: 'IMPLICIT',
					},
					{
						Id: 'Marketing_Email',
						TransactionType: 'NOT_GIVEN',
						ConsentType: 'IMPLICIT',
					},
					{
						Id: 'Transactional_SMS',
						TransactionType: 'CONFIRMED',
						ConsentType: 'IMPLICIT',
					},
					{
						Id: 'Marketing_SMS',
						TransactionType: 'NOT_GIVEN',
						ConsentType: 'IMPLICIT',
					},
				],
				source: 'onetrust-schedule-consultation',
				externalId: externalId,
				dsDataElements: {
					Email: userDetails.Email,
					Mobile: userDetails.Mobile,
					FirstName: userDetails.FirstName,
					LastName: userDetails.LastName,
					DateOfBirth: userDetails.DateOfBirth,
					Address: userDetails.Address,
					City: userDetails.City,
					State: userDetails.State,
					Zip: userDetails.Zip,
				},
			},
		};

		console.log('Sending mutation with variables:', JSON.stringify(variables, null, 2));
		const result = await beffeClient.mutation(CONSENT_MANAGEMENT_MUTATION, variables).toPromise();
		console.log('Mutation result:', result);

		if (result.error) {
			console.error('Consent management error:', result.error);
			return null;
		}

		return result.data as ConsentManagementResponse;
	} catch (error) {
		console.error('Failed to submit consent management:', error);
		return null;
	}
};
